import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { Report, useGetScalingSize } from '@ui/Report';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { OffsetBox } from './OffsetBox';
import {
  getProductGroupSegmentIconName,
  OVRIGA_SEGMENT_ID,
  OVRIGA_SEGMENT_LABEL,
} from './report-product-groups.helpers';
import {
  PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT,
  PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT,
  PRODUCT_GROUPS_LEGEND_ICON_LABEL_GAP_PERCENT,
  PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT,
  PRODUCT_GROUPS_LEGEND_LEFT_PERCENT,
  PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT,
} from './report-product-groups.constants';
import type { ProductGroupSegment } from './report-product-groups.model';
import {
  PRODUCT_GROUPS_LABEL_VALUE,
  PRODUCT_GROUPS_TEXT_COLOR,
  REPORT_SECTION_HEADING,
  SBEF_LEGEND_TEXT_FONT_SIZE,
} from './report-product-groups.typography';
import {
  computeProductGroupsLegendTableColumns,
  placeProductGroupsLegendRows,
} from './product-groups-legend.helpers';

const { Text } = Report;

const LEGEND = {
  textFontSize: SBEF_LEGEND_TEXT_FONT_SIZE,
  labelGap: PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT,
  iconLabelGap: PRODUCT_GROUPS_LEGEND_ICON_LABEL_GAP_PERCENT,
} as const;

type ProductGroupsLegendTableProps = {
  segments: ProductGroupSegment[];
};

/** Vertical legend table for product categories — matches donut slice grouping. */
export const ProductGroupsLegendTable: React.FC<
  ProductGroupsLegendTableProps
> = ({ segments }) => {
  const getScalingSize = useGetScalingSize();
  const { classes, cx } = useStyles({ getScalingSize });

  const placed = placeProductGroupsLegendRows(segments);
  if (placed.length === 0) return null;

  const legendColumns = computeProductGroupsLegendTableColumns(
    placed,
    getScalingSize,
  );
  const firstRowTopPct = placed[0]!.labelTopPct;
  const headingTopPct =
    firstRowTopPct - PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT * 0.8;

  return (
    <Box
      className={classes.root}
      sx={{
        top: `${PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT}%`,
        left: `${PRODUCT_GROUPS_LEGEND_LEFT_PERCENT}%`,
        width: legendColumns.tableWidth,
        height: `${PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT}%`,
        transform: 'translateY(-50%)',
      }}
    >
      <Box
        className={classes.legendOverlay}
        sx={{
          '--product-groups-legend-label-width': legendColumns.labelWidth,
          '--product-groups-legend-co2e-width': legendColumns.co2eWidth,
          '--product-groups-legend-offset-width': legendColumns.offsetWidth,
        }}
      >
        <Box className={classes.heading} sx={{ top: `${headingTopPct}%` }}>
          <Text
            as="h3"
            fontSize={REPORT_SECTION_HEADING.fontSize}
            fontWeight={REPORT_SECTION_HEADING.fontWeight}
            sx={{
              color: PRODUCT_GROUPS_TEXT_COLOR,
              letterSpacing: getScalingSize(
                REPORT_SECTION_HEADING.letterSpacing,
              ),
              lineHeight: REPORT_SECTION_HEADING.lineHeight,
              whiteSpace: 'nowrap',
            }}
          >
            Products
          </Text>
        </Box>
        {placed.map((row) => {
          const offsetValue =
            row.arrowValue != null ? -row.arrowValue : undefined;
          const labelTitle =
            row.detailLabel != null
              ? `${OVRIGA_SEGMENT_LABEL} (${row.detailLabel})`
              : row.label;

          return (
            <Box
              key={row.id}
              className={classes.legendRow}
              sx={{ top: `${row.labelTopPct}%` }}
            >
              <Box
                className={cx(
                  classes.legendEntry,
                  legendColumns.hasOffsetColumn &&
                    classes.legendEntryWithOffset,
                )}
              >
                <Box
                  className={classes.legendLabel}
                  component="span"
                  title={labelTitle}
                >
                  {row.memberIds ? (
                    <Box className={classes.categoryIcons}>
                      {row.memberIds.map((memberId) => (
                        <Box key={memberId} className={classes.categoryIcon}>
                          <Icon
                            category="elementCategory"
                            icon={getProductGroupSegmentIconName(memberId)}
                            size={getScalingSize(LEGEND.textFontSize)}
                            color="text.secondary"
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box className={classes.categoryIcon}>
                      <Icon
                        category="elementCategory"
                        icon={getProductGroupSegmentIconName(row.id)}
                        size={getScalingSize(LEGEND.textFontSize)}
                        color="text.secondary"
                      />
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{ minWidth: 0, overflow: 'hidden' }}
                  >
                    <Text
                      as="span"
                      fontSize={LEGEND.textFontSize}
                      fontWeight={row.id === OVRIGA_SEGMENT_ID ? 400 : 700}
                      sx={{
                        color: PRODUCT_GROUPS_TEXT_COLOR,
                        lineHeight: 1.2,
                        opacity: 0.8,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {row.label}
                    </Text>
                  </Box>
                </Box>
                <Box className={classes.legendCo2e}>
                  <Box className={classes.cloudIcon}>
                    <Icon
                      category="costEmission"
                      icon="kgCO2e"
                      size={getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.iconSize)}
                    />
                  </Box>
                  <Text
                    as="span"
                    fontSize={LEGEND.textFontSize}
                    fontWeight={500}
                    sx={{
                      color: PRODUCT_GROUPS_TEXT_COLOR,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: PRODUCT_GROUPS_LABEL_VALUE.lineHeight,
                      opacity: PRODUCT_GROUPS_LABEL_VALUE.opacity,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getDisplayNumber(row.cloudValue, {
                      decimals: row.cloudValue >= 10 ? 0 : 1,
                    })}
                  </Text>
                </Box>
                {legendColumns.hasOffsetColumn && (
                  <Box className={classes.legendOffset}>
                    {offsetValue != null &&
                      Math.round(Math.abs(offsetValue) * 10) !== 0 && (
                        <OffsetBox
                          value={offsetValue}
                          label={row.label}
                          fontSize={LEGEND.textFontSize}
                        />
                      )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  root: {
    position: 'absolute',
    pointerEvents: 'none',
    overflow: 'visible',
    zIndex: 2,
  },
  legendOverlay: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
  heading: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transform: 'translateY(-100%)',
    paddingLeft: getScalingSize(PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT),
    pointerEvents: 'none',
  },
  legendRow: {
    position: 'absolute',
    left: 0,
    display: 'flex',
    alignItems: 'center',
    width: 'max-content',
    transform: 'translateY(-50%)',
  },
  legendEntry: {
    display: 'grid',
    width: 'max-content',
    gridTemplateColumns:
      'var(--product-groups-legend-label-width) var(--product-groups-legend-co2e-width)',
    alignItems: 'center',
    gap: getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.groupGap),
    paddingLeft: getScalingSize(LEGEND.labelGap),
  },
  legendEntryWithOffset: {
    gridTemplateColumns:
      'var(--product-groups-legend-label-width) var(--product-groups-legend-co2e-width) var(--product-groups-legend-offset-width)',
  },
  legendLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: getScalingSize(LEGEND.iconLabelGap),
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  categoryIcon: {
    display: 'flex',
    flexShrink: 0,
  },
  categoryIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.innerGap),
    flexShrink: 0,
  },
  legendCo2e: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.innerGap),
    minWidth: 0,
    flexShrink: 0,
  },
  legendOffset: {
    display: 'flex',
    justifyContent: 'flex-start',
    minWidth: 0,
    flexShrink: 0,
  },
  cloudIcon: {
    display: 'flex',
    opacity: PRODUCT_GROUPS_LABEL_VALUE.iconOpacity,
  },
}));
